import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useStore } from "../store";
import * as api from "../api";
import { APP_ROUTES } from "../routes";
import type {
  Deployment,
  DeploymentKind,
  RobotManufacturer,
  SafetyEnvelopeConfig,
  ConnectionStatus,
  VlaStats,
} from "../types";

/* ------------------------------------------------------------------ */
/*  Status badge helper                                                */
/* ------------------------------------------------------------------ */

function deploymentBadgeClass(status: Deployment["status"]): string {
  switch (status) {
    case "running":
      return "badge-success";
    case "deploying":
      return "badge-info";
    case "configuring":
      return "badge-warning";
    case "paused":
      return "badge-warning";
    case "stopped":
      return "badge-neutral";
    case "emergency_stopped":
      return "badge-danger";
    case "error":
      return "badge-danger";
    default:
      return "badge-neutral";
  }
}

/** Format an optional millisecond value for display. */
function fmtMs(v?: number): string {
  if (v == null || Number.isNaN(v)) return "—";
  return v >= 100 ? v.toFixed(0) : v.toFixed(1);
}

/** Format an optional count for display. */
function fmtCount(v?: number): string {
  if (v == null || Number.isNaN(v)) return "—";
  return Math.round(v).toLocaleString();
}

function connectionDotClass(status: ConnectionStatus): string {
  switch (status) {
    case "connected":
      return "connected";
    case "disconnected":
    case "unknown":
      return "disconnected";
    case "error":
      return "error";
    default:
      return "disconnected";
  }
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ROBOT_TYPES: { value: RobotManufacturer; label: string }[] = [
  { value: "UR", label: "Universal Robots (UR5/UR10)" },
  { value: "ABB", label: "ABB" },
  { value: "FANUC", label: "FANUC" },
  { value: "KUKA", label: "KUKA" },
  { value: "Franka", label: "Franka Emika" },
  { value: "Other", label: "Other" },
  // Simulated arm with real kinematics; the only connector the envelope has
  // ever fired against. Host is ignored.
  { value: "Sim", label: "Simulated arm (UR5 kinematics)" },
  // Receive-only UR attachment for silent trials. Cannot move the arm.
  { value: "Observer", label: "Observer (UR, receive-only)" },
];

const DEFAULT_SAFETY_CONFIG: SafetyEnvelopeConfig = {
  joint_limits: [
    { joint_name: "shoulder_pan", min_deg: -360, max_deg: 360 },
    { joint_name: "shoulder_lift", min_deg: -360, max_deg: 360 },
    { joint_name: "elbow", min_deg: -360, max_deg: 360 },
    { joint_name: "wrist_1", min_deg: -360, max_deg: 360 },
    { joint_name: "wrist_2", min_deg: -360, max_deg: 360 },
    { joint_name: "wrist_3", min_deg: -360, max_deg: 360 },
  ],
  workspace_bounds: {
    x_min: -1.0,
    x_max: 1.0,
    y_min: -1.0,
    y_max: 1.0,
    z_min: 0.0,
    z_max: 1.5,
  },
  max_velocity: 1.5,
  max_force: 150,
  collision_threshold: 25,
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function DeploymentView() {
  const { id: projectId } = useParams<{ id: string }>();
  const deployments = useStore((s) => s.deployments);
  const trainingJobs = useStore((s) => s.trainingJobs);
  const robotConnections = useStore((s) => s.robotConnections);
  const loading = useStore((s) => s.loading);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);
  const fetchDeployments = useStore((s) => s.fetchDeployments);
  const fetchTrainingJobs = useStore((s) => s.fetchTrainingJobs);
  const fetchRobotConnections = useStore((s) => s.fetchRobotConnections);

  /* robot connection form */
  const [robotType, setRobotType] = useState<RobotManufacturer>("UR");
  const [robotHost, setRobotHost] = useState("");
  // Specific model, e.g. "UR5". Without it the bridge has no kinematics, so
  // a proposed motion cannot be rehearsed against the workspace box and the
  // deployment's signed posture will say exactly that.
  const [robotModel, setRobotModel] = useState("UR5");
  const [robotPort, setRobotPort] = useState("30002");
  const [connecting, setConnecting] = useState(false);
  // The envelope is read-only until somebody says why they are widening it.
  // Free-text safety numbers with no record of who set them make the envelope
  // a suggestion; this puts a named person and a reason in the signed chain
  // first. It does not make the numbers safe -- see the note in the panel.
  const [envelopeUnlocked, setEnvelopeUnlocked] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [overriding, setOverriding] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  /* deployment form */
  const [deploymentKind, setDeploymentKind] = useState<DeploymentKind>("trained_policy");
  // Silent trial: everything runs, nothing is commanded. The only deployment
  // shape a risk committee can approve before any hardware evidence exists.
  const [observeOnly, setObserveOnly] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedRobotId, setSelectedRobotId] = useState("");
  const [vlaPrompt, setVlaPrompt] = useState("");

  /* safety config */
  const [safetyConfig, setSafetyConfig] = useState<SafetyEnvelopeConfig>(
    DEFAULT_SAFETY_CONFIG,
  );

  /* deployment actions */
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /* selected deployment for telemetry */
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);

  /* VLA chunk-pipeline stats for the selected VLA deployment */
  const [vlaStats, setVlaStats] = useState<VlaStats | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchDeployments(projectId);
      fetchTrainingJobs(projectId);
    }
    fetchRobotConnections();
  }, [
    projectId,
    fetchDeployments,
    fetchTrainingJobs,
    fetchRobotConnections,
  ]);

  // Auto-select first completed job and first robot connection
  useEffect(() => {
    const completedJobs = trainingJobs.filter(
      (j) => j.status === "completed" && (j.checkpoint_url ?? j.policy_artifact_url),
    );
    if (!selectedJobId && completedJobs.length > 0) {
      setSelectedJobId(completedJobs[0].id);
    }
  }, [trainingJobs, selectedJobId]);

  useEffect(() => {
    if (!selectedRobotId && robotConnections.length > 0) {
      setSelectedRobotId(robotConnections[0].id);
    }
  }, [robotConnections, selectedRobotId]);

  // Poll for telemetry on active deployments
  useEffect(() => {
    const activeDeployments = deployments.filter(
      (d) => d.status === "running" || d.status === "deploying",
    );
    if (activeDeployments.length === 0 || !projectId) return;

    const interval = setInterval(() => {
      fetchDeployments(projectId);
    }, 2000);

    return () => clearInterval(interval);
  }, [deployments, projectId, fetchDeployments]);

  // Chunk-pipeline stats for the selected VLA deployment: fetch once on
  // selection (the bridge keeps the last episode's stats), and poll while
  // the deployment is actively running.
  const selectedForStats = deployments.find((d) => d.id === selectedDeploymentId);
  const isVlaSelected = selectedForStats?.kind === "vla_generalist";
  const isVlaActive =
    isVlaSelected &&
    (selectedForStats.status === "running" ||
      selectedForStats.status === "deploying");

  useEffect(() => {
    if (!selectedDeploymentId || !isVlaSelected) {
      setVlaStats(null);
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const stats = await api.getVlaStats(selectedDeploymentId);
        if (!cancelled) setVlaStats(stats);
      } catch {
        // Runner not started yet — keep whatever snapshot we have.
      }
    };
    poll();
    if (!isVlaActive) return () => {
      cancelled = true;
    };
    const interval = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedDeploymentId, isVlaSelected, isVlaActive]);

  const handleConnect = useCallback(async () => {
    if (robotType !== "Sim" && !robotHost.trim()) return;

    setConnecting(true);
    setConnectionError(null);

    try {
      const conn = await api.createRobotConnection({
        manufacturer: robotType,
        // A simulated arm has no host; anything non-empty satisfies the API.
        host: robotType === "Sim" ? "sim" : robotHost.trim(),
        port: Number.parseInt(robotPort, 10) || undefined,
        model: robotModel.trim() || undefined,
      });
      setSelectedRobotId(conn.id);
      setRobotHost("");
      await fetchRobotConnections();
    } catch (e) {
      setConnectionError((e as Error).message);
    } finally {
      setConnecting(false);
    }
  }, [robotType, robotHost, robotPort, fetchRobotConnections]);

  const handleDisconnect = useCallback(
    async (robotId: string) => {
      setConnectionError(null);
      try {
        await api.deleteRobotConnection(robotId);
        if (selectedRobotId === robotId) setSelectedRobotId("");
        await fetchRobotConnections();
      } catch (e) {
        setConnectionError((e as Error).message);
      }
    },
    [selectedRobotId, fetchRobotConnections],
  );

  const handleUnlockEnvelope = useCallback(async () => {
    if (!projectId || overrideReason.trim().length < 10) return;
    setOverriding(true);
    setOverrideError(null);
    try {
      await api.recordEnvelopeOverride(projectId, overrideReason.trim());
      setEnvelopeUnlocked(true);
    } catch (e) {
      // Failing closed is the point: if the reason could not be recorded, the
      // fields stay locked rather than being widened off the record.
      setOverrideError((e as Error).message);
    } finally {
      setOverriding(false);
    }
  }, [projectId, overrideReason]);

  const handleCreateDeployment = useCallback(async () => {
    if (!projectId || !selectedRobotId) return;
    if (deploymentKind === "trained_policy" && !selectedJobId) return;
    if (deploymentKind === "vla_generalist" && !vlaPrompt.trim()) return;

    setActionLoading(true);
    setActionError(null);

    try {
      if (deploymentKind === "vla_generalist") {
        await api.createDeployment(projectId, {
          kind: "vla_generalist",
          prompt: vlaPrompt.trim(),
          robot_connection_id: selectedRobotId,
          safety_config: safetyConfig,
          observe_only: observeOnly,
        });
      } else {
        await api.createDeployment(projectId, {
          kind: "trained_policy",
          training_job_id: selectedJobId,
          robot_connection_id: selectedRobotId,
          safety_config: safetyConfig,
          observe_only: observeOnly,
        });
      }
      await fetchDeployments(projectId);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActionLoading(false);
    }
  }, [projectId, deploymentKind, selectedJobId, selectedRobotId, vlaPrompt, safetyConfig, observeOnly, fetchDeployments]);

  const handleStartDeployment = useCallback(
    async (deploymentId: string) => {
      if (!projectId) return;
      setActionLoading(true);
      setActionError(null);
      try {
        await api.startDeployment(projectId, deploymentId);
        await fetchDeployments(projectId);
      } catch (e) {
        setActionError((e as Error).message);
      } finally {
        setActionLoading(false);
      }
    },
    [projectId, fetchDeployments],
  );

  const handleStopDeployment = useCallback(
    async (deploymentId: string) => {
      if (!projectId) return;
      setActionLoading(true);
      setActionError(null);
      try {
        await api.stopDeployment(projectId, deploymentId);
        await fetchDeployments(projectId);
      } catch (e) {
        setActionError((e as Error).message);
      } finally {
        setActionLoading(false);
      }
    },
    [projectId, fetchDeployments],
  );

  const handleEmergencyStop = useCallback(
    async (deploymentId: string) => {
      if (!projectId) return;
      // Emergency stop does not set loading state -- it must fire immediately
      try {
        await api.emergencyStop(projectId, deploymentId);
        await fetchDeployments(projectId);
      } catch (e) {
        setActionError((e as Error).message);
      }
    },
    [projectId, fetchDeployments],
  );

  const updateWorkspaceBound = useCallback(
    (key: keyof SafetyEnvelopeConfig["workspace_bounds"], value: string) => {
      setSafetyConfig((prev) => ({
        ...prev,
        workspace_bounds: {
          ...prev.workspace_bounds,
          [key]: parseFloat(value) || 0,
        },
      }));
    },
    [],
  );

  const completedJobs = trainingJobs.filter(
    (j) => j.status === "completed" && (j.checkpoint_url ?? j.policy_artifact_url),
  );

  const selectedDeployment = deployments.find(
    (d) => d.id === selectedDeploymentId,
  );
  const telemetry = selectedDeployment?.telemetry ?? null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Deployment</h1>
          <p className="page-subtitle">
            Connect to robots and deploy trained policies
          </p>
        </div>
      </div>

      {/* Error banners */}
      {error && (
        <div
          className="card mb-4"
          style={{ borderColor: "var(--color-danger)" }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: "var(--color-danger)" }}>{error}</span>
            <button className="btn btn-sm btn-secondary" onClick={clearError}>
              Dismiss
            </button>
          </div>
        </div>
      )}
      {actionError && (
        <div
          className="card mb-4"
          style={{ borderColor: "var(--color-danger)" }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: "var(--color-danger)" }}>{actionError}</span>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setActionError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {/* Robot Connection */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            Robot Connection
          </h2>

          {connectionError && (
            <div
              style={{
                color: "var(--color-danger)",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {connectionError}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="robot-type">
              Robot Type
            </label>
            <select
              id="robot-type"
              className="form-select"
              value={robotType}
              onChange={(e) =>
                setRobotType(e.target.value as RobotManufacturer)
              }
            >
              {ROBOT_TYPES.map((rt) => (
                <option key={rt.value} value={rt.value}>
                  {rt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="robot-model">
              Model
            </label>
            <input
              id="robot-model"
              className="form-input"
              type="text"
              placeholder="UR5, UR10, LR Mate 200iD, IRB 120"
              value={robotModel}
              onChange={(e) => setRobotModel(e.target.value)}
            />
            <span className="text-sm text-muted">
              Needed for kinematics. Without it, motions cannot be rehearsed
              against the workspace box and the record will say so.
            </span>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="robot-host">
              Host / IP Address
            </label>
            <input
              id="robot-host"
              className="form-input"
              type="text"
              value={robotHost}
              onChange={(e) => setRobotHost(e.target.value)}
              placeholder="e.g., 192.168.1.100"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="robot-port">
              Port
            </label>
            <input
              id="robot-port"
              className="form-input"
              type="number"
              value={robotPort}
              onChange={(e) => setRobotPort(e.target.value)}
              placeholder="30002"
            />
          </div>

          <div className="flex gap-2">
            <button
              className="btn btn-primary"
              onClick={handleConnect}
              disabled={connecting || (robotType !== "Sim" && !robotHost.trim())}
            >
              {connecting ? "Connecting..." : "Connect"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => fetchRobotConnections()}
            >
              Refresh
            </button>
          </div>

          {/* Active connections */}
          {robotConnections.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div
                className="text-sm"
                style={{ fontWeight: 600, marginBottom: 8 }}
              >
                Active Connections
              </div>
              {robotConnections.map((conn) => (
                <div
                  key={conn.id}
                  className="flex justify-between items-center"
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 500 }}>
                      {conn.manufacturer} {conn.model}
                    </span>
                    <span className="text-sm text-muted" style={{ marginLeft: 8 }}>
                      {conn.ip_address}:{conn.port}
                    </span>
                  </div>
                  <div className="connection-indicator">
                    <span
                      className={`connection-dot ${connectionDotClass(conn.status)}`}
                    />
                    {conn.status}
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginLeft: 12 }}
                      onClick={() => handleDisconnect(conn.id)}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Safety Configuration */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            Safety Configuration
          </h2>

          <p
            className="text-sm text-muted"
            style={{ marginBottom: 12, lineHeight: 1.5 }}
          >
            These limits are advisory. Speed, force and safe-axis limiting are
            certified functions of the robot&rsquo;s own controller (UR Safety
            Planes, ABB SafeMove, FANUC DCS) and are what actually stop the
            arm. What you set here is clamped server-side to the vendor
            ceiling, signed into the ledger as <code>envelope.admitted</code>,
            and used as input to commissioning &mdash; not as a substitute for
            it.
          </p>

          {!envelopeUnlocked ? (
            <div
              className="mb-4"
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                padding: 12,
              }}
            >
              <div className="text-sm" style={{ marginBottom: 8 }}>
                Locked. The values below are in force. Widening them is
                allowed, but it is recorded.
              </div>
              <textarea
                className="form-input"
                rows={2}
                placeholder="Why does this cell need a wider envelope? (recorded against your name)"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
              {overrideError && (
                <div className="text-sm" style={{ color: "var(--color-danger)", marginTop: 6 }}>
                  Could not record the override, so the fields stay locked: {overrideError}
                </div>
              )}
              <button
                className="btn btn-secondary mt-2"
                disabled={overrideReason.trim().length < 10 || overriding}
                onClick={handleUnlockEnvelope}
              >
                {overriding ? "Recording\u2026" : "Record reason and unlock"}
              </button>
            </div>
          ) : (
            <div className="text-sm mb-4" style={{ color: "var(--color-warning)" }}>
              Unlocked. This change is on the record.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Workspace Bounds (meters)</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
              }}
            >
              {(
                [
                  ["x_min", "X Min"],
                  ["x_max", "X Max"],
                  ["y_min", "Y Min"],
                  ["y_max", "Y Max"],
                  ["z_min", "Z Min"],
                  ["z_max", "Z Max"],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <span
                    className="text-sm text-muted"
                    style={{ display: "block", marginBottom: 2 }}
                  >
                    {label}
                  </span>
                  <input
                    className="form-input"
                    disabled={!envelopeUnlocked}
                    type="number"
                    step="0.1"
                    value={safetyConfig.workspace_bounds[key]}
                    onChange={(e) => updateWorkspaceBound(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 16,
            }}
          >
            <div className="form-group">
              <label className="form-label" htmlFor="max-velocity">
                Max Velocity (m/s)
              </label>
              <input
                id="max-velocity"
                className="form-input"
                disabled={!envelopeUnlocked}
                type="number"
                step="0.1"
                min="0"
                value={safetyConfig.max_velocity}
                onChange={(e) =>
                  setSafetyConfig((prev) => ({
                    ...prev,
                    max_velocity: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="max-force">
                Max Force (N)
              </label>
              <input
                id="max-force"
                className="form-input"
                disabled={!envelopeUnlocked}
                type="number"
                step="5"
                min="0"
                value={safetyConfig.max_force}
                onChange={(e) =>
                  setSafetyConfig((prev) => ({
                    ...prev,
                    max_force: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="collision-threshold">
                Force Threshold (N)
              </label>
              <input
                id="collision-threshold"
                className="form-input"
                disabled={!envelopeUnlocked}
                type="number"
                step="1"
                min="0"
                value={safetyConfig.collision_threshold}
                onChange={(e) =>
                  setSafetyConfig((prev) => ({
                    ...prev,
                    collision_threshold: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
          </div>

          {/* Joint limits (collapsible summary) */}
          <div className="form-group">
            <label className="form-label">Joint Limits (degrees)</label>
            <div style={{ display: "grid", gap: 6 }}>
              {safetyConfig.joint_limits.map((jl, idx) => (
                <div
                  key={jl.joint_name}
                  className="flex items-center gap-2"
                  style={{ fontSize: 13 }}
                >
                  <span
                    style={{
                      width: 120,
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {jl.joint_name}
                  </span>
                  <input
                    className="form-input"
                    disabled={!envelopeUnlocked}
                    type="number"
                    style={{ width: 80 }}
                    value={jl.min_deg}
                    onChange={(e) => {
                      const limits = [...safetyConfig.joint_limits];
                      limits[idx] = {
                        ...limits[idx],
                        min_deg: parseFloat(e.target.value) || 0,
                      };
                      setSafetyConfig((prev) => ({
                        ...prev,
                        joint_limits: limits,
                      }));
                    }}
                  />
                  <span className="text-muted">to</span>
                  <input
                    className="form-input"
                    disabled={!envelopeUnlocked}
                    type="number"
                    style={{ width: 80 }}
                    value={jl.max_deg}
                    onChange={(e) => {
                      const limits = [...safetyConfig.joint_limits];
                      limits[idx] = {
                        ...limits[idx],
                        max_deg: parseFloat(e.target.value) || 0,
                      };
                      setSafetyConfig((prev) => ({
                        ...prev,
                        joint_limits: limits,
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Deploy new policy */}
      <div className="card mb-4">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Deploy Policy
        </h2>

        {/* Deployment kind toggle */}
        <div className="form-group">
          <label className="form-label">Deployment kind</label>
          <div className="flex gap-4" style={{ marginTop: 4 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="deployment-kind"
                value="trained_policy"
                checked={deploymentKind === "trained_policy"}
                onChange={() => setDeploymentKind("trained_policy")}
              />
              Trained checkpoint
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="deployment-kind"
                value="vla_generalist"
                checked={deploymentKind === "vla_generalist"}
                onChange={() => setDeploymentKind("vla_generalist")}
              />
              VLA generalist (zero-shot)
            </label>
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={observeOnly}
              onChange={(e) => setObserveOnly(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>Observe only (silent trial).</strong>{" "}
              <span className="text-sm text-muted">
                Run the whole stack — planner, schedule, envelope, telemetry —
                and never command the robot. You get a signed record of every
                clamp that <em>would</em> have fired, with no mechanism of harm.
              </span>
            </span>
          </label>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 16,
            alignItems: "end",
          }}
        >
          {/* Conditional: checkpoint selector or VLA prompt */}
          {deploymentKind === "trained_policy" ? (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="policy-select">
                Trained Policy (Checkpoint)
              </label>
              <select
                id="policy-select"
                className="form-select"
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
              >
                {completedJobs.length === 0 && (
                  <option value="">No completed training jobs</option>
                )}
                {completedJobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name} ({job.model_type} - {job.scenario})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="vla-prompt">
                Task prompt
              </label>
              <textarea
                id="vla-prompt"
                className="form-textarea"
                rows={3}
                value={vlaPrompt}
                onChange={(e) => setVlaPrompt(e.target.value)}
                placeholder="pick the red block and place it on the blue tray"
              />
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="robot-select">
              Target Robot
            </label>
            <select
              id="robot-select"
              className="form-select"
              value={selectedRobotId}
              onChange={(e) => setSelectedRobotId(e.target.value)}
            >
              {robotConnections.length === 0 && (
                <option value="">No robots connected</option>
              )}
              {robotConnections
                .filter((r) => r.status === "connected")
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.manufacturer} {r.model} ({r.ip_address})
                  </option>
                ))}
            </select>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleCreateDeployment}
            disabled={
              actionLoading ||
              !selectedRobotId ||
              (deploymentKind === "trained_policy" && !selectedJobId) ||
              (deploymentKind === "vla_generalist" && !vlaPrompt.trim())
            }
          >
            {actionLoading
              ? "Deploying..."
              : deploymentKind === "vla_generalist"
                ? "Deploy generalist policy"
                : "Create Deployment"}
          </button>
        </div>
      </div>

      {/* Deployments list */}
      <div className="card mb-4">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Deployments
        </h2>

        {loading && deployments.length === 0 ? (
          <p className="text-muted">Loading deployments...</p>
        ) : deployments.length === 0 ? (
          <p className="text-muted">
            No deployments yet. Deploy a trained policy above.
          </p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Kind</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((dep) => (
                  <tr
                    key={dep.id}
                    style={{
                      cursor: "pointer",
                      background:
                        selectedDeploymentId === dep.id
                          ? "var(--color-accent-muted)"
                          : undefined,
                    }}
                    onClick={() =>
                      setSelectedDeploymentId(
                        selectedDeploymentId === dep.id ? null : dep.id,
                      )
                    }
                  >
                    <td style={{ fontWeight: 500, fontFamily: "monospace" }}>
                      {dep.id.slice(0, 8)}
                    </td>
                    <td>
                      {dep.kind === "vla_generalist" ? (
                        <span className="badge badge-info">VLA generalist</span>
                      ) : (
                        <span className="badge badge-neutral">checkpoint</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${deploymentBadgeClass(dep.status)}`}
                      >
                        {dep.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(dep.created_at).toLocaleString()}
                    </td>
                    <td>
                      <div
                        className="flex gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(dep.status === "created" ||
                          dep.status === "configuring" ||
                          dep.status === "stopped" ||
                          dep.status === "paused") && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleStartDeployment(dep.id)}
                            disabled={actionLoading}
                          >
                            Start
                          </button>
                        )}
                        {(dep.status === "running" ||
                          dep.status === "deploying") && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleStopDeployment(dep.id)}
                            disabled={actionLoading}
                          >
                            Stop
                          </button>
                        )}
                        {dep.status === "running" && (
                          <button
                            className="emergency-stop-btn"
                            style={{
                              padding: "5px 14px",
                              fontSize: 12,
                            }}
                            onClick={() => handleEmergencyStop(dep.id)}
                          >
                            E-STOP
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Emergency stop (global) */}
      {deployments.some((d) => d.status === "running") && (
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <button
            className="emergency-stop-btn"
            onClick={() => {
              const running = deployments.filter(
                (d) => d.status === "running",
              );
              for (const dep of running) {
                handleEmergencyStop(dep.id);
              }
            }}
          >
            Emergency Stop All
          </button>
        </div>
      )}

      {/* Telemetry display */}
      {selectedDeployment && telemetry && (
        <div className="card">
          <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, flex: 1 }}>
              Telemetry: Deployment {selectedDeployment.id.slice(0, 8)}
            </h2>
            {/* The signed record is the product; telemetry is just what fed it. */}
            <Link
              to={APP_ROUTES.evidence(projectId ?? "", selectedDeployment.id)}
              className="btn btn-secondary"
            >
              Evidence pack →
            </Link>
          </div>

          <div className="telemetry-grid">
            {telemetry.joint_positions.map((pos, i) => (
              <div key={`joint-${i}`} className="telemetry-item">
                <div className="telemetry-value">{pos.toFixed(2)}</div>
                <div className="telemetry-label">Joint {i + 1} (deg)</div>
              </div>
            ))}
            {telemetry.joint_forces.map((force, i) => (
              <div key={`force-${i}`} className="telemetry-item">
                <div className="telemetry-value">{force.toFixed(1)}</div>
                <div className="telemetry-label">Force {i + 1} (N)</div>
              </div>
            ))}
            <div className="telemetry-item">
              <div className="telemetry-value">
                {telemetry.reward.toFixed(3)}
              </div>
              <div className="telemetry-label">Reward</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">{telemetry.step_count}</div>
              <div className="telemetry-label">Steps</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {telemetry.safety_margin.toFixed(2)}
              </div>
              <div className="telemetry-label">Safety Margin</div>
            </div>
          </div>
        </div>
      )}

      {/* VLA chunk-pipeline stats */}
      {selectedDeployment?.kind === "vla_generalist" && vlaStats && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
            VLA Pipeline: Deployment {selectedDeployment.id.slice(0, 8)}
          </h2>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            Real-time action chunking — the model plans short trajectories
            while the robot executes the previous one, so slow inference
            never stalls the control loop.
          </p>

          <div
            className="text-sm"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            Inference latency
          </div>
          <div className="telemetry-grid" style={{ marginBottom: 16 }}>
            <div className="telemetry-item">
              <div className="telemetry-value">{fmtMs(vlaStats.p50_ms)}</div>
              <div className="telemetry-label">p50 (ms)</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">{fmtMs(vlaStats.p95_ms)}</div>
              <div className="telemetry-label">p95 (ms)</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtMs(vlaStats.est_compute_ms)}
              </div>
              <div className="telemetry-label">Model compute (ms)</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtMs(vlaStats.est_network_ms)}
              </div>
              <div className="telemetry-label">Network (ms)</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtMs(vlaStats.ewma_jitter_ms)}
              </div>
              <div className="telemetry-label">Jitter (ms)</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {vlaStats.decisions_per_sec != null
                  ? vlaStats.decisions_per_sec.toFixed(1)
                  : "—"}
              </div>
              <div className="telemetry-label">Decisions / s</div>
            </div>
          </div>

          <div
            className="text-sm"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            Chunk schedule
          </div>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtCount(vlaStats.chunks_merged)}
              </div>
              <div className="telemetry-label">Chunks merged</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtCount(vlaStats.knots_overwritten)}
              </div>
              <div className="telemetry-label">Knots overwritten</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtCount(vlaStats.control_ticks)}
              </div>
              <div className="telemetry-label">Control ticks</div>
            </div>
            <div
              className="telemetry-item"
              style={
                (vlaStats.underruns ?? 0) > 0
                  ? { borderColor: "var(--color-warning)" }
                  : undefined
              }
            >
              <div className="telemetry-value">
                {fmtCount(vlaStats.underruns)}
                {vlaStats.control_ticks
                  ? ` (${(((vlaStats.underruns ?? 0) / vlaStats.control_ticks) * 100).toFixed(1)}%)`
                  : ""}
              </div>
              <div className="telemetry-label">Underruns</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">
                {fmtCount(vlaStats.low_confidence_rejects)}
              </div>
              <div className="telemetry-label">Low-conf rejects</div>
            </div>
            <div className="telemetry-item">
              <div className="telemetry-value">{fmtCount(vlaStats.samples)}</div>
              <div className="telemetry-label">Decisions</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
